import { io, type Socket } from 'socket.io-client';
import type { Ack, ClientEvents, ServerEvents } from '@shared/types';
import { getDeviceId } from './storage';

export type GameSocket = Socket<ServerEvents, ClientEvents>;

let socket: GameSocket | null = null;

/** One socket per tab, same origin as the page (Vite proxies it in development). */
export function getSocket(): GameSocket {
  if (!socket) {
    socket = io({ transports: ['websocket', 'polling'], reconnectionDelayMax: 4000, auth: { deviceId: getDeviceId() } });
  }
  return socket;
}

/** Reconnect so the server sees a new sign-in (or sign-out); open rooms resume on 'connect'. */
export function reconnectSocket(): void {
  if (socket?.connected) {
    socket.disconnect();
    socket.connect();
  }
}

type Payload<E extends keyof ClientEvents> = Parameters<ClientEvents[E]>[0];
type Result<E extends keyof ClientEvents> = Parameters<Parameters<ClientEvents[E]>[1]>[0];

/** Emit an event and wait for the server's ack. Never rejects; failures come back as `{ ok: false }`. */
export function call<E extends keyof ClientEvents>(event: E, payload: Payload<E>, timeoutMs = 8000): Promise<Result<E>> {
  const s = getSocket();
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false, error: 'The server is taking too long. Check your connection.' } as Result<E>), timeoutMs);
    const emit = s.emit as unknown as (e: string, p: unknown, cb: (r: Ack<object>) => void) => void;
    emit.call(s, event, payload, (r: Ack<object>) => {
      clearTimeout(timer);
      resolve(r as Result<E>);
    });
  });
}
